/* eslint-disable react-refresh/only-export-components */
'use client'

import { Avatar } from '@base-ui/react/avatar'
import { cva, VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@eigtech/ui/lib/utils'

const avatarStatusVariants = cva(
  'border-background flex size-2 items-center rounded-full border-2',
  {
    variants: {
      variant: {
        online: 'bg-green-600',
        offline: 'bg-zinc-600 dark:bg-zinc-300',
        busy: 'bg-yellow-600',
        away: 'bg-blue-600',
      },
    },
    defaultVariants: {
      variant: 'online',
    },
  }
)

// Base UI Avatar Root
function AvatarRoot({ className, ...props }: React.ComponentProps<typeof Avatar.Root>) {
  return (
    <Avatar.Root
      className={cn('relative flex h-10 w-10 shrink-0', className)}
      data-slot="avatar"
      {...props}
    />
  )
}

// Base UI Avatar Image
function AvatarImage({ className, ...props }: React.ComponentProps<typeof Avatar.Image>) {
  return (
    <Avatar.Image
      className={cn('aspect-square h-full w-full overflow-hidden rounded-full', className)}
      data-slot="avatar-image"
      {...props}
    />
  )
}

// Base UI Avatar Fallback
function AvatarFallback({ className, ...props }: React.ComponentProps<typeof Avatar.Fallback>) {
  return (
    <Avatar.Fallback
      className={cn(
        'bg-muted text-muted-foreground flex h-full w-full items-center justify-center overflow-hidden rounded-full text-sm font-medium',
        className
      )}
      data-slot="avatar-fallback"
      {...props}
    />
  )
}

function AvatarIndicator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('absolute flex size-6 items-center justify-center', className)}
      data-slot="avatar-indicator"
      {...props}
    />
  )
}

function AvatarStatus({
  className,
  variant,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof avatarStatusVariants>) {
  return (
    <div
      className={cn(avatarStatusVariants({ variant }), className)}
      data-slot="avatar-status"
      {...props}
    />
  )
}

function getInitials(name: string) {
  const names = name.trim().split(' ')
  const firstName = names[0] != null ? names[0] : ''
  const lastName = names.length > 1 ? names[names.length - 1] : ''
  return firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : firstName.charAt(0)
}

// Exports with proper naming to match Base UI pattern
export {
  AvatarRoot as Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarIndicator,
  AvatarStatus,
  avatarStatusVariants,
  getInitials,
}
